import React, { useState, useEffect } from 'react';
import MDEditor from '@uiw/react-md-editor';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import './General.css'; // <--- CSS Import

const General = ({ procedureData, onCancel }) => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [editingProcedureId, setEditingProcedureId] = useState(null);
  const [subCategories, setSubCategories] = useState([]);
  const [showNewSubCategory, setShowNewSubCategory] = useState(false);
  const [newSubCategoryName, setNewSubCategoryName] = useState('');
  const [types, setTypes] = useState([]);
  const [showNewTypeInput, setShowNewTypeInput] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [userLabId, setUserLabId] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    script: '',
    customId: '',
    quickCode: '',
    category: '',
    newCategoryName: '',
    subCategory: '',
    type: '',
    procedureGroups: '',
    procedureCategory: '',
    procedureDepartment: '',
    sortRank: '',
    unitCount: '',
    tariffCode: '',
    labOnly: false,
    inactive: false,
    salesDescription: '',
    newGroup: '',
    trafficCode: '',
    description: '',
    overrideDefault: false,
    turnAroundDays: '',
    ignoreInbound: false,
    ignoreOutbound: false,
    autoArrive: 'no',
    placeOnHold: 'no',
    treatmentLength: '',
    isLotRequired: false,
    allowNoteForLot: false,
    isSerialRequired: false,
    allowNoteForSerial: false,
    traceabilityValue: '',
  });

  // Function to show success alert
  const showSuccessAlert = (isUpdate = false) => {
    Swal.fire({
      title: 'Success!',
      text: isUpdate ? 'Procedure updated successfully!' : 'Procedure created successfully!',
      icon: 'success',
      confirmButtonText: 'OK',
      confirmButtonColor: '#004aad',
      customClass: {
        popup: 'custom-swal-popup',
        title: 'custom-swal-title',
        confirmButton: 'custom-swal-confirm-button'
      }
    });
  };

  // Function to show error alert
  const showErrorAlert = (errorMessage) => {
    Swal.fire({
      title: 'Error!',
      text: errorMessage,
      icon: 'error',
      confirmButtonText: 'OK',
      confirmButtonColor: '#dc3545'
    });
  };

  // Function to show confirmation alert for cancel
  const showCancelConfirmation = (onConfirm) => {
    Swal.fire({
      title: 'Clear Form Data?',
      text: 'Do you want to clear the current form data? If you cancel, the data will remain for editing.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, clear it!',
      cancelButtonText: 'No, keep it',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
    }).then((result) => {
      if (result.isConfirmed) {
        if (onConfirm) {
          onConfirm();
        }
      }
    });
  };

  // Get userLabId from localStorage on component mount
  useEffect(() => {
    const getUserLabId = () => {
      try {
        const userLabIdFromStorage = localStorage.getItem('userLabId');
        
        if (userLabIdFromStorage) {
          setUserLabId(userLabIdFromStorage);
          return userLabIdFromStorage;
        }
        
        const userData = localStorage.getItem('userData');
        if (userData) {
          try {
            const parsedData = JSON.parse(userData);
            
            if (parsedData.labId) {
              setUserLabId(parsedData.labId);
              localStorage.setItem('userLabId', parsedData.labId);
              return parsedData.labId;
            }
            
            if (parsedData.userLabId) {
              setUserLabId(parsedData.userLabId);
              localStorage.setItem('userLabId', parsedData.userLabId);
              return parsedData.userLabId;
            }
            
            if (parsedData.id) {
              setUserLabId(parsedData.id);
              localStorage.setItem('userLabId', parsedData.id);
              return parsedData.id;
            }
          } catch (parseError) {
            // Silently handle parse error
          }
        }
        
        const possibleKeys = ['labId', 'userId', 'labUserId', 'currentLabId'];
        for (const key of possibleKeys) {
          const value = localStorage.getItem(key);
          if (value) {
            setUserLabId(value);
            localStorage.setItem('userLabId', value);
            return value;
          }
        }
        
        return '';
      } catch (error) {
        return '';
      }
    };
    
    const labId = getUserLabId();
    if (!labId) {
      // Could not retrieve userLabId from localStorage
    }
  }, []);

  // Fetch categories based on userLabId
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        let url = 'https://hms.backend.hiragroupcloud.com/api/categories';
        
        if (userLabId) {
          url += `?userLabId=${encodeURIComponent(userLabId)}`;
        }
        
        const response = await axios.get(url);
        setCategories(response.data);
      } catch (error) {
        setCategories([]);
      }
    };

    if (userLabId) {
      fetchCategories();
    } else {
      setCategories([]);
    }
  }, [userLabId]);

  // Fetch sub-categories based on selected category AND userLabId
  useEffect(() => {
    const fetchSubCategories = async () => {
      if (formData.category && formData.category !== 'New Category') {
        try {
          let url = `https://hms.backend.hiragroupcloud.com/api/sub-categories?category=${encodeURIComponent(formData.category)}`;
          
          if (userLabId) {
            url += `&userLabId=${encodeURIComponent(userLabId)}`;
          }
          
          const response = await axios.get(url);
          const subCatNames = response.data.map(item => item.name);
          setSubCategories(subCatNames);
        } catch (error) {
          setSubCategories([]);
        }
      } else {
        setSubCategories([]);
      }
    };

    if (formData.category && userLabId) {
      fetchSubCategories();
    } else {
      setSubCategories([]);
    }
  }, [formData.category, userLabId]);

  // Fetch types based on selected sub-category AND userLabId
  useEffect(() => {
    const fetchTypes = async () => {
      if (formData.subCategory && userLabId) {
        try {
          let url = `https://hms.backend.hiragroupcloud.com/api/types?subCategory=${encodeURIComponent(formData.subCategory)}`;
          
          if (userLabId) {
            url += `&userLabId=${encodeURIComponent(userLabId)}`;
          }
          
          const response = await axios.get(url);
          const typeNames = response.data.map(item => item.name);
          setTypes(typeNames);
        } catch (error) {
          setTypes([]);
        }
      } else {
        setTypes([]);
      }
    };

    if (formData.subCategory && userLabId) {
      fetchTypes();
    } else {
      setTypes([]);
    }
  }, [formData.subCategory, userLabId]);

  // Load procedure data from sessionStorage
  useEffect(() => {
    const savedProcedureId = sessionStorage.getItem('procedureId');
    if (savedProcedureId && !procedureData) {
      fetchProcedureData(savedProcedureId);
    }
  }, []);

  const fetchProcedureData = async (procedureId) => {
    try {
      const response = await axios.get(`https://hms.backend.hiragroupcloud.com/api/procedures/${procedureId}`);
      const procedure = response.data;
      
      setFormData({
        name: procedure.name || '',
        script: procedure.script || '',
        customId: procedure.customId || '',
        quickCode: procedure.quickCode || '',
        category: procedure.category || '',
        newCategoryName: procedure.newCategoryName || '',
        subCategory: procedure.subCategory || '',
        type: procedure.type || '',
        procedureCategory: procedure.procedureCategory || '',
        procedureGroups: procedure.procedureGroups || '',
        procedureDepartment: procedure.procedureDepartment || '',
        sortRank: procedure.sortRank || '',
        unitCount: procedure.unitCount || '',
        tariffCode: procedure.tariffCode || '',
        labOnly: procedure.labOnly || false,
        inactive: procedure.inactive || false,
        salesDescription: procedure.salesDescription || '',
        newGroup: procedure.newGroup || '',
        trafficCode: procedure.trafficCode || '',
        description: procedure.description || '',
        overrideDefault: procedure.overrideDefault || false,
        turnAroundDays: procedure.turnAroundDays || '',
        ignoreInbound: procedure.ignoreInbound || false,
        ignoreOutbound: procedure.ignoreOutbound || false,
        autoArrive: procedure.autoArrive || 'no',
        placeOnHold: procedure.placeOnHold || 'no',
        treatmentLength: procedure.treatmentLength || '',
        isLotRequired: procedure.isLotRequired || false,
        allowNoteForLot: procedure.allowNoteForLot || false,
        isSerialRequired: procedure.isSerialRequired || false,
        allowNoteForSerial: procedure.allowNoteForSerial || false,
        traceabilityValue: procedure.traceabilityValue || '',
      });
      setShowNewCategory(procedure.category === 'New Category');
      setEditingProcedureId(procedure._id || null);
    } catch (error) {
      // Silently handle error
    }
  };

  useEffect(() => {
    if (procedureData) {
      setFormData({
        name: procedureData.name || '',
        script: procedureData.script || '',
        customId: procedureData.customId || '',
        quickCode: procedureData.quickCode || '',
        category: procedureData.category || '',
        newCategoryName: procedureData.newCategoryName || '',
        subCategory: procedureData.subCategory || '',
        type: procedureData.type || '',
        procedureCategory: procedureData.procedureCategory || '',
        procedureGroups: procedureData.procedureGroups || '',
        procedureDepartment: procedureData.procedureDepartment || '',
        sortRank: procedureData.sortRank || '',
        unitCount: procedureData.unitCount || '',
        tariffCode: procedureData.tariffCode || '',
        labOnly: procedureData.labOnly || false,
        inactive: procedureData.inactive || false,
        salesDescription: procedureData.salesDescription || '',
        newGroup: procedureData.newGroup || '',
        trafficCode: procedureData.trafficCode || '',
        description: procedureData.description || '',
        overrideDefault: procedureData.overrideDefault || false,
        turnAroundDays: procedureData.turnAroundDays || '',
        ignoreInbound: procedureData.ignoreInbound || false,
        ignoreOutbound: procedureData.ignoreOutbound || false,
        autoArrive: procedureData.autoArrive || 'no',
        placeOnHold: procedureData.placeOnHold || 'no',
        treatmentLength: procedureData.treatmentLength || '',
        isLotRequired: procedureData.isLotRequired || false,
        allowNoteForLot: procedureData.allowNoteForLot || false,
        isSerialRequired: procedureData.isSerialRequired || false,
        allowNoteForSerial: procedureData.allowNoteForSerial || false,
        traceabilityValue: procedureData.traceabilityValue || '',
      });
      setShowNewCategory(procedureData.category === 'New Category');
      setEditingProcedureId(procedureData._id || null);
    }
  }, [procedureData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleCategoryChange = (e) => {
    const { value } = e.target;
    setFormData({
      ...formData,
      category: value,
      newCategoryName: value === 'New Category' ? formData.newCategoryName : '',
      subCategory: '',
      type: '',
    });
    setShowNewCategory(value === 'New Category');
    setSubCategories([]);
    setTypes([]);
  };

  const handleQuillChange = (value) => {
    setFormData((prevData) => ({
      ...prevData,
      salesDescription: value,
    }));
  };

  const handleCheckboxChange = (e) => {
    if (e.target.checked) {
      setFormData({
        ...formData,
        overrideDefault: true,
      });
    } else {
      Swal.fire({
        title: 'Delete Custom Scheduling Rules?',
        text: 'Are you sure you want to delete your custom scheduling rules?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete them!',
        cancelButtonText: 'No, keep them',
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
      }).then((result) => {
        if (result.isConfirmed) {
          setFormData({
            ...formData,
            overrideDefault: false,
            turnAroundDays: '',
          });
        } else {
          setFormData({
            ...formData,
            overrideDefault: true,
          });
        }
      });
    }
  };

  const handleAddSubCategory = async () => {
    if (!newSubCategoryName.trim()) {
      showErrorAlert('Please enter a sub-category name.');
      return;
    }

    if (!userLabId) {
      showErrorAlert('User identification required. Please login again.');
      return;
    }

    try {
      await axios.post('https://hms.backend.hiragroupcloud.com/api/sub-categories', {
        name: newSubCategoryName,
        category: formData.category,
        addedBy: userLabId
      });
      
      // Refresh sub-categories list
      const url = `https://hms.backend.hiragroupcloud.com/api/sub-categories?category=${encodeURIComponent(formData.category)}&userLabId=${userLabId}`;
      const refreshResponse = await axios.get(url);
      const subCatNames = refreshResponse.data.map(item => item.name);
      
      setSubCategories(subCatNames);
      setFormData({ ...formData, subCategory: newSubCategoryName });
      setNewSubCategoryName('');
      setShowNewSubCategory(false);
      
      Swal.fire({
        title: 'Success!',
        text: 'Sub-category added successfully!',
        icon: 'success',
        confirmButtonText: 'OK',
        confirmButtonColor: '#004aad'
      });
      
    } catch (error) {
      if (error.response && error.response.status === 400) {
        showErrorAlert(error.response.data.error);
      } else {
        showErrorAlert('Failed to add sub-category. Please try again.');
      }
    }
  };

  const handleAddType = async () => {
    if (!newTypeName.trim()) {
      showErrorAlert('Please enter a type name.');
      return;
    }

    if (!userLabId) {
      showErrorAlert('User identification required. Please login again.');
      return;
    }

    try {
      await axios.post('https://hms.backend.hiragroupcloud.com/api/types', {
        name: newTypeName,
        subCategory: formData.subCategory,
        addedBy: userLabId
      });
      
      // Refresh types list
      const url = `https://hms.backend.hiragroupcloud.com/api/types?subCategory=${encodeURIComponent(formData.subCategory)}&userLabId=${userLabId}`;
      const refreshResponse = await axios.get(url);
      const typeNames = refreshResponse.data.map(item => item.name);
      
      setTypes(typeNames);
      setFormData({ ...formData, type: newTypeName });
      setNewTypeName('');
      setShowNewTypeInput(false);
      
      Swal.fire({
        title: 'Success!',
        text: `Type "${newTypeName}" added successfully!`,
        icon: 'success',
        confirmButtonText: 'OK',
        confirmButtonColor: '#004aad'
      });
      
    } catch (error) {
      if (error.response && error.response.status === 400) {
        showErrorAlert(`Error: ${error.response.data.error}`);
      } else {
        showErrorAlert('Failed to add type. Please try again.');
      }
    }
  };

  const handleDeleteSubCategory = async () => {
    if (!formData.subCategory) {
      showErrorAlert('Please select a sub-category to delete.');
      return;
    }
    
    if (!userLabId) {
      showErrorAlert('User identification required. Please login again.');
      return;
    }
    
    Swal.fire({
      title: 'Delete Sub-Category?',
      text: `Are you sure you want to delete ${formData.subCategory}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'No, cancel',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(
            `https://hms.backend.hiragroupcloud.com/api/sub-categories/${formData.subCategory}?category=${formData.category}&addedBy=${userLabId}`
          );
          
          const url = `https://hms.backend.hiragroupcloud.com/api/sub-categories?category=${encodeURIComponent(formData.category)}&userLabId=${userLabId}`;
          const refreshResponse = await axios.get(url);
          const subCatNames = refreshResponse.data.map(item => item.name);
          
          setSubCategories(subCatNames);
          setFormData({ ...formData, subCategory: '', type: '' });
          setTypes([]);
          
          Swal.fire({
            title: 'Deleted!',
            text: 'Sub-category deleted successfully!',
            icon: 'success',
            confirmButtonText: 'OK',
            confirmButtonColor: '#004aad'
          });
        } catch (error) {
          showErrorAlert('Failed to delete sub-category. Please try again.');
        }
      }
    });
  };

  const handleDeleteType = async () => {
    if (!formData.type) {
      showErrorAlert('Please select a type to delete.');
      return;
    }
    
    if (!userLabId) {
      showErrorAlert('User identification required. Please login again.');
      return;
    }
    
    Swal.fire({
      title: 'Delete Type?',
      text: `Are you sure you want to delete ${formData.type}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'No, cancel',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(
            `https://hms.backend.hiragroupcloud.com/api/types/${formData.type}?subCategory=${formData.subCategory}&addedBy=${userLabId}`
          );
          
          const url = `https://hms.backend.hiragroupcloud.com/api/types?subCategory=${encodeURIComponent(formData.subCategory)}&userLabId=${userLabId}`;
          const refreshResponse = await axios.get(url);
          const typeNames = refreshResponse.data.map(item => item.name);
          
          setTypes(typeNames);
          setFormData({ ...formData, type: '' });
          
          Swal.fire({
            title: 'Deleted!',
            text: 'Type deleted successfully!',
            icon: 'success',
            confirmButtonText: 'OK',
            confirmButtonColor: '#004aad'
          });
        } catch (error) {
          showErrorAlert('Failed to delete type. Please try again.');
        }
      }
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      script: '',
      customId: '',
      quickCode: '',
      category: '',
      newCategoryName: '',
      subCategory: '',
      type: '',
      procedureGroups: '',
      procedureCategory: '',
      procedureDepartment: '',
      sortRank: '',
      unitCount: '',
      tariffCode: '',
      labOnly: false,
      inactive: false,
      salesDescription: '',
      newGroup: '',
      trafficCode: '',
      description: '',
      overrideDefault: false,
      turnAroundDays: '',
      ignoreInbound: false,
      ignoreOutbound: false,
      autoArrive: 'no',
      placeOnHold: 'no',
      treatmentLength: '',
      isLotRequired: false,
      allowNoteForLot: false,
      isSerialRequired: false,
      allowNoteForSerial: false,
      traceabilityValue: '',
    });
    setShowNewCategory(false);
    setEditingProcedureId(null);
    setShowNewSubCategory(false);
    setNewSubCategoryName('');
    setShowNewTypeInput(false);
    setNewTypeName('');
    setSubCategories([]);
    setTypes([]);
    sessionStorage.removeItem('procedureId');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    Swal.fire({
      title: 'Please wait...',
      text: 'Saving procedure data',
      allowOutsideClick: false,
      showConfirmButton: false,
      willOpen: () => {
        Swal.showLoading();
      }
    });
    
    let currentUserLabId = localStorage.getItem('userLabId');
    
    if (!currentUserLabId) {
      currentUserLabId = localStorage.getItem('labId');
    }
    
    if (!currentUserLabId) {
      const userData = localStorage.getItem('userData');
      if (userData) {
        try {
          const parsedData = JSON.parse(userData);
          currentUserLabId = parsedData.labId || parsedData.userLabId || parsedData.id;
        } catch (e) {
          // Silently handle parse error
        }
      }
    }
    
    if (!currentUserLabId && !editingProcedureId) {
      Swal.close();
      showErrorAlert('User identification (userLabId) is required. Please login again.');
      return;
    }
    
    try {
      const payload = {
        ...formData,
        sortRank: formData.sortRank ? parseInt(formData.sortRank) : 0,
        turnAroundDays: formData.turnAroundDays ? parseInt(formData.turnAroundDays) : '',
        userLabId: currentUserLabId
      };
      
      let response;
      
      if (editingProcedureId) {
        response = await axios.put(
          `https://hms.backend.hiragroupcloud.com/api/procedures/${editingProcedureId}`,
          payload
        );
        
        Swal.close();
        showSuccessAlert(true);
        
      } else {
        response = await axios.post('https://hms.backend.hiragroupcloud.com/api/procedures', payload);

        if (response.data && response.data._id) {
          sessionStorage.setItem('procedureId', response.data._id);
          setEditingProcedureId(response.data._id);
        }
        
        Swal.close();
        showSuccessAlert(false);
      }
      
    } catch (error) {
      Swal.close();
      
      if (error.response && error.response.data && error.response.data.error) {
        showErrorAlert(`Failed to submit form: ${error.response.data.error}`);
      } else {
        showErrorAlert('Failed to submit form. Please try again.');
      }
    }
  };

  const handleCancel = () => {
    showCancelConfirmation(() => {
      resetForm();
      if (onCancel) {
        onCancel();
      }
    });
  };

  return (
    <div className="gen-container">
      <style>
        {`
          .custom-swal-popup {
            border-radius: 10px;
            border: 2px solid #004aad;
            box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
          }
          
          .custom-swal-title {
            color: #004aad;
            font-weight: bold;
            font-size: 20px;
          }
          
          .custom-swal-confirm-button {
            background-color: #004aad !important;
            border-radius: 5px;
            padding: 8px 20px;
          }
          
          .custom-swal-confirm-button:hover {
            background-color: #003580 !important;
          }
          
          .swal2-icon.swal2-success {
            border-color: #004aad;
            color: #004aad;
          }
          
          .swal2-icon.swal2-success .swal2-success-ring {
            border: 4px solid rgba(0, 74, 173, 0.2);
          }
          
          .swal2-icon.swal2-success [class^='swal2-success-line'] {
            background-color: #004aad;
          }
          
          .swal2-icon.swal2-error {
            border-color: #dc3545;
            color: #dc3545;
          }
          
          .swal2-icon.swal2-error [class^='swal2-x-mark-line'] {
            background-color: #dc3545;
          }
          
          .swal2-icon.swal2-warning {
            border-color: #ffc107;
            color: #ffc107;
          }
          
          .swal2-icon.swal2-question {
            border-color: #004aad;
            color: #004aad;
          }
        `}
      </style>
      
      <form onSubmit={handleSubmit}>
        <div className="gen-header">
          <h4 className="gen-title">Procedure Classification</h4>
          <div className="gen-header-btns">
            <button
              type="submit"
              className="gen-btn-primary"
              disabled={!editingProcedureId && !userLabId}
              title={!editingProcedureId && !userLabId ? "User identification required" : ""}
            >
              {editingProcedureId ? 'Update' : 'Save'}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="gen-btn-cancel"
            >
              Cancel
            </button>
          </div>
        </div>
        
        {/* Procedure Classification Section */}
        <div className="gen-section">
          <div className="gen-row">
            <label htmlFor="category" className="gen-label">
              Category <span style={{ color: 'red' }}>*</span>
            </label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleCategoryChange}
              className="gen-input"
              required
              disabled={!userLabId}
            >
              <option value="">Select Category</option>
              <option value="Fixed Restoration">Fixed Restoration</option>
              <option value="Removable Restoration">Removable Restoration</option>
              <option value="Orthodontic Appliance">Orthodontic Appliance</option>
              <option value="Implant Restoration">Implant Restoration</option>
              <option value="Sundry">Sundry</option>
            </select>
            {!userLabId && (
              <span className="gen-note gen-note-error">
                Login required to load categories
              </span>
            )}
            {userLabId && categories.length === 0 && (
              <span className="gen-note gen-note-muted">
                No categories found for your lab. You can create new ones.
              </span>
            )}
          </div>
          
          {formData.category && (
            <div className="gen-row">
              <label htmlFor="subCategory" className="gen-label">
                Sub-Category <span style={{ color: 'red' }}>*</span>
              </label>
              <select
                id="subCategory"
                name="subCategory"
                value={formData.subCategory}
                onChange={handleChange}
                className="gen-input"
                required
                disabled={!userLabId}
              >
                <option value="">Select Sub-Category</option>
                {subCategories.map((subCat) => (
                  <option key={subCat} value={subCat}>
                    {subCat}
                  </option>
                ))}
              </select>
              {formData.subCategory && (
                <button
                  type="button"
                  onClick={handleDeleteSubCategory}
                  className="gen-btn-danger"
                >
                  Delete
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowNewSubCategory(true)}
                className="gen-btn-info"
                disabled={!userLabId}
              >
                Add
              </button>
            </div>
          )}
          
          {showNewSubCategory && (
            <div className="gen-row gen-row-indent">
              <input
                type="text"
                value={newSubCategoryName}
                onChange={(e) => setNewSubCategoryName(e.target.value)}
                placeholder="Enter new sub-category"
                className="gen-input gen-input-new"
              />
              <button
                type="button"
                onClick={handleAddSubCategory}
                className="gen-btn-info"
              >
                Save
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowNewSubCategory(false);
                  setNewSubCategoryName('');
                }}
                className="gen-btn-gray"
              >
                Cancel
              </button>
            </div>
          )}
          
          {formData.subCategory && (
            <div className="gen-row">
              <label htmlFor="type" className="gen-label">
                Type <span style={{ color: 'red' }}>*</span>
              </label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="gen-input"
                required
                disabled={!userLabId}
              >
                <option value="">Select Type</option>
                {types.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              {formData.type && (
                <button
                  type="button"
                  onClick={handleDeleteType}
                  className="gen-btn-danger"
                >
                  Delete
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowNewTypeInput(true)}
                className="gen-btn-info"
                disabled={!userLabId}
              >
                Add
              </button>
            </div>
          )}
          
          {showNewTypeInput && (
            <div className="gen-row gen-row-indent">
              <input
                type="text"
                value={newTypeName}
                onChange={(e) => setNewTypeName(e.target.value)}
                placeholder="Enter new type"
                className="gen-input gen-input-new"
              />
              <button
                type="button"
                onClick={handleAddType}
                className="gen-btn-info"
              >
                Save
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowNewTypeInput(false);
                  setNewTypeName('');
                }}
                className="gen-btn-gray"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
       
        <h4 className="gen-title">Display</h4>
        <div className="gen-section">
          <div className="gen-row">
            <label htmlFor="name" className="gen-label">
              Name <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="gen-input"
              required
            />
          </div>
          <div className="gen-row">
            <label htmlFor="script" className="gen-label">
              Description <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              id="script"
              name="script"
              value={formData.script}
              onChange={handleChange}
              className="gen-input"
              required
            />
          </div>
          <div className="gen-row">
            <label htmlFor="customId" className="gen-label">CustomID</label>
            <input
              type="text"
              id="customId"
              name="customId"
              value={formData.customId}
              onChange={handleChange}
              className="gen-input"
            />
          </div>
          <div className="gen-row">
            <label htmlFor="quickCode" className="gen-label">Quick Code</label>
            <input
              type="text"
              id="quickCode"
              name="quickCode"
              value={formData.quickCode}
              onChange={handleChange}
              className="gen-input"
            />
          </div>
          <div className="gen-row">
            <label htmlFor="procedureCategory" className="gen-label">
              Category <span style={{ color: 'red' }}>*</span>
            </label>
            <select
              id="procedureCategory"
              name="procedureCategory"
              value={formData.procedureCategory}
              onChange={handleChange}
              className="gen-input"
              required
            >
              <option value="">Select Category</option>
              {categories.length > 0 ? (
                categories.map((cat) => (
                  <option key={cat._id} value={cat.name}>
                    {cat.name}
                  </option>
                ))
              ) : (
                <option value="" disabled>No categories available</option>
              )}
            </select>
          </div>
          <div className="gen-row">
            <label htmlFor="procedureGroups" className="gen-label">Procedure Group</label>
            <select
              id="procedureGroups"
              name="procedureGroups"
              value={formData.procedureGroups}
              onChange={handleChange}
              className="gen-input"
            >
              <option value="">Select Option</option>
              <option value="Group1">New Group</option>
            </select>
          </div>
          {formData.procedureGroups === 'Group1' && (
            <div className="gen-row">
              <label htmlFor="newGroup" className="gen-label">
                New Group <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                id="newGroup"
                name="newGroup"
                value={formData.newGroup}
                onChange={handleChange}
                className="gen-input"
                required
              />
            </div>
          )}
          <div className="gen-row">
            <label htmlFor="procedureDepartment" className="gen-label">Procedure Department</label>
            <select
              id="procedureDepartment"
              name="procedureDepartment"
              value={formData.procedureDepartment}
              onChange={handleChange}
              className="gen-input"
            >
              <option value="">Select Procedure Department</option>
              <option value="Department1">Department 1</option>
              <option value="Department2">Department 2</option>
            </select>
          </div>
          <div className="gen-row">
            <label htmlFor="sortRank" className="gen-label">Sort Rank</label>
            <input
              type="number"
              id="sortRank"
              name="sortRank"
              value={formData.sortRank}
              onChange={handleChange}
              className="gen-input"
            />
          </div>
          <div className="gen-row">
            <label htmlFor="unitCount" className="gen-label">Unit Count</label>
            <select
              id="unitCount"
              name="unitCount"
              value={formData.unitCount}
              onChange={handleChange}
              className="gen-input"
            >
              <option value="">Select Unit Count</option>
              <option value="Count by Teeth">Count by Teeth</option>
              <option value="Count by Units">Count by Units</option>
            </select>
          </div>
          <div className="gen-row">
            <label htmlFor="trafficCode" className="gen-label">Traffic Code</label>
            <input
              type="text"
              id="trafficCode"
              name="trafficCode"
              value={formData.trafficCode}
              onChange={handleChange}
              className="gen-input"
            />
          </div>
          <div className="gen-row">
            <label htmlFor="labOnly" className="gen-label">Lab Only</label>
            <input
              type="checkbox"
              id="labOnly"
              name="labOnly"
              checked={formData.labOnly}
              onChange={handleChange}
              className="gen-checkbox"
            />
          </div>
          <div className="gen-row">
            <label htmlFor="inactive" className="gen-label">Inactive</label>
            <input
              type="checkbox"
              id="inactive"
              name="inactive"
              checked={formData.inactive}
              onChange={handleChange}
              className="gen-checkbox"
            />
          </div>
          <p className="gen-sub-title">Sale Description</p>
          <p className="gen-help-text">
            Provide an optional extended sales-oriented description about this procedure.
          </p>
          <MDEditor
            theme="snow"
            value={formData.salesDescription}
            onChange={handleQuillChange}
            placeholder="Enter your ad description here..."
            className="gen-editor"
          />
        </div>

        <h4 className="gen-title">Scheduling</h4>
        <div className="gen-section">
          <div className="gen-row">
            <label className="gen-label gen-label-alt">
              Default Turn Around
            </label>
            <p className="gen-inline-text">
              {formData.overrideDefault && formData.turnAroundDays ? (
                <>
                  {formData.turnAroundDays} day(s)
                  <hr className="gen-hr" />
                </>
              ) : (
                '10 day(s)'
              )}
            </p>
          </div>
          <div className="gen-block">
            <div className="gen-row gen-row-tight">
              <label htmlFor="overrideDefault" className="gen-label gen-label-bold">
                Override Default
              </label>
              <input
                type="checkbox"
                id="overrideDefault"
                name="overrideDefault"
                checked={formData.overrideDefault}
                onChange={handleCheckboxChange}
                className="gen-checkbox gen-checkbox-override"
              />
            </div>
            {formData.overrideDefault && (
              <div className="gen-override-box">
                <label className="gen-override-label">
                  Turn Around Days
                </label>
                <input
                  type="number"
                  name="turnAroundDays"
                  value={formData.turnAroundDays}
                  onChange={handleChange}
                  className="gen-input-sm"
                />
              </div>
            )}
          </div>
          <div className="gen-row">
            <label className="gen-label gen-label-link">
              Select Tasks
            </label>
          </div>
          <div className="gen-note-block">
            <p className="gen-italic-text">
              By ignoring inbound transit days you are indicating that this is a digital procedure and that files are required to be uploaded.
            </p>
          </div>
          <div className="gen-row">
            <label htmlFor="ignoreInbound" className="gen-label">Ignore Inbound Transit Days</label>
            <input
              type="checkbox"
              id="ignoreInbound"
              name="ignoreInbound"
              checked={formData.ignoreInbound}
              onChange={handleChange}
              className="gen-checkbox"
            />
          </div>
          <div className="gen-row">
            <label htmlFor="ignoreOutbound" className="gen-label">Ignore Outbound Transit Days</label>
            <input
              type="checkbox"
              id="ignoreOutbound"
              name="ignoreOutbound"
              checked={formData.ignoreOutbound}
              onChange={handleChange}
              className="gen-checkbox"
            />
          </div>
          <div className="gen-row">
            <label htmlFor="autoArrive" className="gen-label">Auto Arrive</label>
            <select
              id="autoArrive"
              name="autoArrive"
              value={formData.autoArrive}
              onChange={handleChange}
              className="gen-input"
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
          <div className="gen-row">
            <label htmlFor="placeOnHold" className="gen-label">Place on Hold</label>
            <select
              id="placeOnHold"
              name="placeOnHold"
              value={formData.placeOnHold}
              onChange={handleChange}
              className="gen-input"
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
          <div className="gen-row">
            <label htmlFor="treatmentLength" className="gen-label">Treatment Length</label>
            <input
              type="text"
              id="treatmentLength"
              name="treatmentLength"
              value={formData.treatmentLength}
              onChange={handleChange}
              className="gen-input"
            />
          </div>
        </div>

        <h4 className="gen-title">Traceability</h4>
        <div className="gen-section gen-section-trace">
          <div className="gen-row">
            <label className="gen-label gen-label-alt">
              Description of material, or part
            </label>
            <input
              type="text"
              placeholder="Enter Value Name"
              name="traceabilityValue"
              value={formData.traceabilityValue}
              onChange={handleChange}
              className="gen-input gen-input-trace"
            />
          </div>
          <div className="gen-row">
            <label className="gen-label gen-label-alt">
              Lot #
            </label>
            <div className="gen-check-group">
              <label className="gen-check-item">
                <input
                  type="checkbox"
                  checked={formData.isLotRequired}
                  onChange={handleChange}
                  name="isLotRequired"
                />
                <span>Is Required</span>
              </label>
              <label className="gen-check-item">
                <input
                  type="checkbox"
                  checked={formData.allowNoteForLot}
                  onChange={handleChange}
                  name="allowNoteForLot"
                  disabled={!formData.isLotRequired}
                />
                <span>Allow Note</span>
              </label>
            </div>
          </div>
          <div className="gen-row">
            <label className="gen-label gen-label-alt">
              Serial #
            </label>
            <div className="gen-check-group">
              <label className="gen-check-item">
                <input
                  type="checkbox"
                  checked={formData.isSerialRequired}
                  onChange={handleChange}
                  name="isSerialRequired"
                />
                <span>Is Required</span>
              </label>
              <label className="gen-check-item">
                <input
                  type="checkbox"
                  checked={formData.allowNoteForSerial}
                  onChange={handleChange}
                  name="allowNoteForSerial"
                  disabled={!formData.isSerialRequired}
                />
                <span>Allow Note</span>
              </label>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default General;